/**
 * Yelp Integration Module (Story 23.5)
 *
 * Barrel export + side-effect adapter registration. Registration happens
 * here — NOT in `yelp.ts` — to prevent the Failure 5a anti-pattern
 * documented in `google/index.ts`. Importing this module is sufficient to
 * register the adapter; importing `yelp.ts` directly would give you the
 * adapter object without registration.
 */

import { registerAdapter } from "../registry";
import { yelpAdapter } from "./yelp";

// Side-effect registration (Story 23.5)
registerAdapter("yelp", yelpAdapter);

// Public surface — adapter + canonical constants
export {
  yelpAdapter,
  YELP_PLATFORM_VALUES,
  YELP_API_VERSION,
  YELP_API_BASE,
  validateYelpConnection,
  type YelpPlatform,
  type YelpMetadata,
} from "./yelp";

// Metrics + dimensions
export { YELP_METRICS, YELP_DIMENSIONS } from "./metric-allowlist";

// API helpers
export {
  fetchYelpBusinessDetails,
  fetchYelpReviews,
  validateYelpApiKey,
  type YelpBusinessDetails,
  type YelpBusinessLocation,
  type YelpBusinessCoordinates,
  type YelpBusinessCategory,
  type YelpReview,
  type YelpReviewUser,
  type YelpReviewsResponse,
} from "./api";

// Errors
export {
  parseYelpApiError,
  sanitizeYelpErrorMessage,
  buildYelpAdapterError,
  YELP_QPS_BURST_BACKOFF_MS,
  YELP_QPS_BURST_CODE,
  YELP_DAILY_QUOTA_CODE,
  YelpApiCallError,
  type YelpApiError,
} from "./errors";

// Safe fetch
export {
  safeFetchYelp,
  type YelpSafeFetchContext,
} from "./safe-fetch";

// PII
export {
  YELP_PII_FIELDS,
  sanitizeYelpPii,
  stripCsvInjectionPrefix,
} from "./pii";

// Transformers
export {
  transformYelpBusinessDetails,
  transformYelpReview,
  transformYelpRatingSnapshot,
  buildYelpReviewAggregation,
  yelpSentimentBucket,
  type YelpReportType,
} from "./transformers";

// Fields (per-endpoint allowlist for build-time test)
export {
  YELP_BUSINESS_DETAILS_FIELDS,
  YELP_REVIEWS_FIELDS,
  YELP_DERIVED_FIELDS,
  allYelpFields,
} from "./fields";

// Cache policy
export { YELP_CACHE_TTL_SEC, YELP_HAS_RETENTION_PREFLIGHT } from "./cache-policy";

// Snapshots
export {
  writeYelpRatingSnapshot,
  readYelpRatingSnapshots,
  readYelpRatingSnapshotPriorDay,
  utcYyyyMmDd,
  type YelpD1Binding,
  type YelpD1PreparedStatement,
  type YelpD1BoundStatement,
  type WriteYelpRatingSnapshotInput,
  type ReadYelpRatingSnapshotsInput,
  type ReadYelpRatingSnapshotPriorDayInput,
  type YelpRatingSnapshotRow,
} from "./snapshots";

// Daily-call budget
export {
  checkYelpDailyBudget,
  incrementYelpDailyBudget,
  checkYelpHealthcheckBudget,
  incrementYelpHealthcheckBudget,
  secondsUntilUtcMidnight,
  utcDateKey,
  YELP_DAILY_BUDGET_CEILING,
  type YelpBudgetKv,
  type CheckYelpDailyBudgetResult,
} from "./budget";
